import React, {
  // useRef,
  // useEffect,
  useState,
  useMemo,
} from 'react';
// import classnames from 'classnames';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
// import map from 'lodash/map';
import Tab from 'react-bootstrap/Tab';
import { Link, Element } from 'react-scroll';
import { ShoppingBag } from 'lucide-react';

import { buildPresetItemFromMenu } from 'utils/preset.util';
import { priceDecimalFormatter } from 'formatter/preset.formatter';
import TruncateWrapper from 'components/common/TruncateWrapper';
import { store } from 'react-notifications-component';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
function isMenuAddedToCart(checkoutData, menuId) {
  return checkoutData.some(obj => obj.menuId === menuId);
}

function getQuantity(checkoutData, menuId) {
  const menuObj = checkoutData.find(obj => obj.menuId === menuId);
  if (menuObj && typeof menuObj.quantity === 'number') {
    return menuObj.quantity;
  }
  return 0;
}

const MenuItemRow = ({ menu, checkoutData, onAddClick }) => {
  const { name, mainImageUrl, dietaryTypes, priceCents, description } = menu;
  const inCart = isMenuAddedToCart(checkoutData, menu.id);
  return (
    <div
      className={`rounded-2xl p-4 sm:p-5 border transition-all flex flex-col sm:flex-row gap-4 justify-between ${
        inCart
          ? 'border-[#E53935]/40 bg-panda-tint/30'
          : 'border-gray-200/80 bg-white hover:border-[#E53935]/50 hover:shadow-md'
      }`}
    >
      <div className="flex gap-4 flex-1">
        <img
          src={mainImageUrl || '/meal_default.png'}
          alt="menu_img"
          className="w-full sm:w-28 h-28 sm:h-24 rounded-xl object-cover flex-shrink-0 bg-gray-100 cursor-pointer"
          onClick={onAddClick}
          referrerPolicy="no-referrer"
          onError={e => {
            e.target.onerror = null;
            e.target.src = '/meal_default.png';
          }}
        />
        <div>
          <h4 className="font-heading font-bold text-base sm:text-lg text-[#202124] leading-snug">
            {name}
          </h4>
          {dietaryTypes && (
            <ul className="flex flex-wrap gap-1.5 mt-1 mb-0 list-none p-0">
              {dietaryTypes.split(',').map(
                dietaryType =>
                  dietaryType && (
                    <li
                      key={dietaryType}
                      className="px-2 py-0.5 rounded-full bg-panda-tint text-[#E53935] text-[10px] font-semibold uppercase tracking-wide"
                    >
                      {dietaryType}
                    </li>
                  ),
              )}
            </ul>
          )}
          <p className="text-xs sm:text-sm text-gray-600 mt-1.5 leading-relaxed">
            <TruncateWrapper text={description} />
          </p>
        </div>
      </div>
      <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:w-36 flex-shrink-0 pt-2 sm:pt-0 border-t border-gray-100 sm:border-0">
        <span className="font-heading font-bold text-base sm:text-lg text-[#202124]">
          ${priceDecimalFormatter(priceCents)}
        </span>
        <button
          className={`px-4 py-2 rounded-xl border font-heading font-bold text-xs transition-all ${
            inCart
              ? 'bg-[#E53935] border-[#E53935] text-white hover:bg-[#d32f2f]'
              : 'border-panda-soft/50 bg-panda-tint/80 hover:bg-[#E53935] hover:text-white hover:border-[#E53935] text-[#E53935]'
          }`}
          type="button"
          onClick={onAddClick}
        >
          {inCart ? 'Update Item' : 'Add Item'}
        </button>
        {inCart && (
          <div className="inline-flex items-center gap-1.5 w-full sm:w-auto justify-center px-3 py-1 rounded-full bg-[#E53935] text-white text-[11px] font-bold uppercase tracking-wide">
            <ShoppingBag className="w-3 h-3" />
            {getQuantity(checkoutData, menu.id)} in cart
          </div>
        )}
      </div>
    </div>
  );
};

const RestaurantMenu = ({
  chefs,
  presetItems,
  setCurrentPresetItem,
  mainHeading,
  checkoutData,
  canonicalLink,
}) => {
  const [activeChef, setActiveChef] = useState(
    chefs && chefs.length > 0 ? chefs[0].id : 0,
  );
  const itemLimit = chefs[0]?.addonMaximumItemsQty;
  const memoizedCategories = useMemo(
    () =>
      chefs.map(chef =>
        groupBy(
          sortBy(chef.menus?.filter(menu => menu?.isDeleted !== true) || [], [
            'priceCents',
          ]),
          'categoryName',
        ),
      ),
    [chefs, presetItems],
  );

  const menuCategoryNames = useMemo(
    () => {
      const categoryNames = activeChef === chefs[0].id
        ? chefs[0].menuCategoryNames
        : chefs[1].menuCategoryNames;

      // Filter out menu categories that have no items
      const activeChefIndex = activeChef === chefs[0].id ? 0 : 1;
      const categoriesWithItems = memoizedCategories[activeChefIndex];

      return categoryNames?.filter(category => {
        const menus = categoriesWithItems?.[category];
        return menus && menus.length > 0;
      }) || [];
    },
    [memoizedCategories, activeChef, chefs],
  );
  const showAddondata = canonicalLink?.pathname;

  const handleAddClick = menu => {
    if (showAddondata && checkoutData?.length === itemLimit) {
      store.addNotification({
        message: `You cannot add more than ${itemLimit} items.`,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    setCurrentPresetItem(buildPresetItemFromMenu(menu));
  };

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200/80 shadow-xs space-y-4">
      <h2 className="text-2xl font-heading font-extrabold text-[#202124] border-b border-gray-100 pb-3">
        {mainHeading}
      </h2>

      <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-1 hide-scrollbar">
        {menuCategoryNames.map(category => (
          <Link
            key={category}
            to={category}
            spy
            smooth
            offset={-90}
            activeClass="category-pill--active"
            className="category-pill flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider transition-all bg-gray-100 hover:bg-gray-200 text-gray-700 cursor-pointer"
          >
            {category}
          </Link>
        ))}
      </div>

      <Tab.Container activeKey={activeChef} onSelect={k => setActiveChef(k)}>
        <Tab.Content>
          {chefs.map((chef, index) => (
            <Tab.Pane eventKey={chef.id} key={chef.id}>
              <div className="space-y-6">
                {menuCategoryNames.map(category => {
                  const menus = memoizedCategories[index][category];
                  return (
                    <Element
                      key={`${chef.id}_${category}`}
                      name={category}
                      className="scroll-mt-24"
                    >
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        {category}
                      </p>
                      <div className="space-y-3">
                        {menus &&
                          menus.length > 0 &&
                          menus.map(menu => (
                            <MenuItemRow
                              key={menu.id}
                              menu={menu}
                              checkoutData={checkoutData}
                              onAddClick={() => handleAddClick(menu)}
                            />
                          ))}
                      </div>
                    </Element>
                  );
                })}
              </div>
            </Tab.Pane>
          ))}
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

RestaurantMenu.propTypes = {
  chefs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      menus: PropTypes.arrayOf(PropTypes.shape({})),
      menuCategoryNames: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  presetItems: PropTypes.arrayOf(PropTypes.shape({})),
  setCurrentPresetItem: PropTypes.func,
  mainHeading: PropTypes.string,
  checkoutData: PropTypes.array.isRequired,
};

RestaurantMenu.defaultProps = {
  chefs: [],
  presetItems: [],
  setCurrentPresetItem: () => {},
  mainHeading: 'Menu',
};
export default RestaurantMenu;
